import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AlertCircle, ArrowDownAZ, CalendarDays, ChevronDown, ChevronUp, Loader2, Search, Star, Tag } from "lucide-react";

import ArchiveSearchResultCard from "../ArchiveSearchResultCard";
import Button from "../ui/Button";
import Typography from "../ui/Typography";
import { ARCHIVE_SEARCH_SORTS, isSemanticMode, isStaleSemanticReason } from "../../services/search/archiveSearchProtocol";

const SortButton = ({ active, icon: Icon, children, onClick }) => (
  <Button
    type="button"
    variant={active ? "default" : "filter"}
    size="sm"
    onClick={onClick}
    aria-pressed={active}
    className="min-h-10"
  >
    <Icon className="h-4 w-4" aria-hidden="true" />
    {children}
  </Button>
);

SortButton.propTypes = {
  active: PropTypes.bool.isRequired,
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
};

function ArchiveSearchResultsContent({
  changeSort,
  clearSearchInput,
  clearTags,
  emptyStateTags,
  fallbackReason,
  isInWatchlist,
  results,
  searchResult,
  searchStatus,
  selectTag,
  selectedTagDetails,
  semanticFallback,
  showBlockingLoading,
  showsLikeTitle,
  state,
  submitSemantic,
  toggleWatchlist,
  unknownSemanticSlugs,
}) {
  const [showAllResults, setShowAllResults] = useState(false);
  const displayLimit = searchResult?.displayLimit || 8;
  const totalResults = results.length;
  const visibleResults = useMemo(
    () => (showAllResults ? results : results.slice(0, displayLimit)),
    [displayLimit, results, showAllResults]
  );
  const hiddenResultCount = Math.max(0, totalResults - displayLimit);
  const hasExpandableResults = hiddenResultCount > 0;

  return (
    <div className="space-y-4">
      {(totalResults > 0 || searchStatus === "semantic-loading") && (
      <div className="archive-results-toolbar">
        <div className="space-y-1">
          <Typography variant="h4" className="text-foreground">
            {searchStatus === "semantic-loading"
              ? "Searching by meaning..."
              : showsLikeTitle
                ? `Shows like ${showsLikeTitle}`
                : `${totalResults} archive ${totalResults === 1 ? "match" : "matches"}`}
          </Typography>
          <Typography variant="body-small" className="text-muted-foreground">
            {fallbackReason && !isStaleSemanticReason(fallbackReason)
              ? "Meaning search is unavailable right now. Showing keyword and tag matches instead."
              : showsLikeTitle
                ? `${totalResults} ${totalResults === 1 ? "show" : "shows"} from the archive with a similar feel.`
                : isSemanticMode(searchResult?.mode)
                ? "Meaning matches blended with keyword and tag matches."
                : "Instant matches from titles, tags, platforms, months, and summaries."}
          </Typography>
          {searchStatus !== "semantic-loading" && hasExpandableResults && (
            <Typography variant="body-small" className="text-muted-foreground">
              Showing {visibleResults.length} of {totalResults} results.
            </Typography>
          )}
        </div>
        <div className="archive-results-toolbar__sort">
          <SortButton active={state.sort === ARCHIVE_SEARCH_SORTS.RELEVANCE} icon={Search} onClick={() => changeSort(ARCHIVE_SEARCH_SORTS.RELEVANCE)}>
            Relevance
          </SortButton>
          <SortButton active={state.sort === ARCHIVE_SEARCH_SORTS.NEWEST} icon={CalendarDays} onClick={() => changeSort(ARCHIVE_SEARCH_SORTS.NEWEST)}>
            Newest
          </SortButton>
          <SortButton active={state.sort === ARCHIVE_SEARCH_SORTS.RATING} icon={Star} onClick={() => changeSort(ARCHIVE_SEARCH_SORTS.RATING)}>
            Rating
          </SortButton>
        </div>
      </div>
      )}

      {selectedTagDetails.length > 0 && state.query && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Typography variant="body-small" className="text-foreground">
            Only shows tagged {selectedTagDetails.map((tag) => `\u201c${tag.tag}\u201d`).join(", ")}.
          </Typography>
          <Button type="button" variant="filter" size="sm" className="min-h-9" onClick={clearTags}>
            Search all shows
          </Button>
        </div>
      )}

      {semanticFallback && (
        <div className="flex flex-col gap-3 rounded-lg border border-warning/60 bg-warning/10 p-4 sm:flex-row sm:items-center sm:justify-between">
          <Typography variant="body-small" className="text-foreground">
            Meaning search could not be reached. You can keep browsing these matches or try again.
          </Typography>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="warning" size="sm" onClick={submitSemantic}>
              Try again
            </Button>
            <Button type="button" variant="filter" size="sm" onClick={clearSearchInput}>
              Clear search
            </Button>
          </div>
        </div>
      )}

      {unknownSemanticSlugs.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-warning/60 bg-warning/10 p-3 text-body-small text-foreground">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Search results are updating. Some matches are temporarily hidden.</span>
        </div>
      )}

      {showBlockingLoading ? (
        <div className="flex min-h-48 items-center justify-center rounded-xl border border-dashed border-accent-gold/60 bg-card/50">
          <Typography variant="body-small" className="inline-flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Searching the archive
          </Typography>
        </div>
      ) : totalResults > 0 ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
            {visibleResults.map((show, indexValue) => (
              <ArchiveSearchResultCard
                key={show.slug}
                show={show}
                isInWatchlist={isInWatchlist}
                position={indexValue + 1}
                onToggleWatchlist={toggleWatchlist}
              />
            ))}
          </div>
          {hasExpandableResults && (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-10 gap-2"
                onClick={() => setShowAllResults((current) => !current)}
                aria-expanded={showAllResults}
              >
                {showAllResults ? (
                  <>
                    <ChevronUp className="h-4 w-4" aria-hidden="true" />
                    Show fewer
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                    Show {hiddenResultCount} more
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-accent-gold/70 bg-card/60 p-6 text-center sm:p-8">
          <ArrowDownAZ className="mx-auto mb-3 h-6 w-6 text-primary" aria-hidden="true" />
          <Typography variant="h4" className="text-foreground">
            No archive matches
          </Typography>
          <Typography variant="body-small" className="mx-auto mt-2 max-w-xl text-muted-foreground">
            Try a broader mood, search for a platform, or start with one of these tastes.
          </Typography>
          {emptyStateTags.length > 0 && (
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {emptyStateTags.map((tag) => (
                <Button
                  key={tag.slug}
                  type="button"
                  variant="filter"
                  size="sm"
                  className="min-h-9 gap-2 px-3 py-1.5 text-body-small"
                  onClick={() => selectTag(tag.slug)}
                >
                  <Tag className="h-3.5 w-3.5" aria-hidden="true" />
                  {tag.tag}
                </Button>
              ))}
            </div>
          )}
          <Button type="button" variant="outline" size="sm" className="mt-5" onClick={clearSearchInput}>
            Clear search
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ArchiveSearchResults(props) {
  const searchResetKey = [
    props.searchResult?.mode || "",
    props.searchResult?.displayLimit || "",
    props.searchResult?.topScore || "",
    props.state.query || "",
    Array.isArray(props.state.selectedTags) ? props.state.selectedTags.join(",") : "",
    props.state.sort || "",
  ].join("|");

  return <ArchiveSearchResultsContent key={searchResetKey} {...props} />;
}

ArchiveSearchResultsContent.propTypes = {
  changeSort: PropTypes.func.isRequired,
  clearSearchInput: PropTypes.func.isRequired,
  clearTags: PropTypes.func.isRequired,
  emptyStateTags: PropTypes.arrayOf(PropTypes.shape({
    slug: PropTypes.string.isRequired,
    tag: PropTypes.string.isRequired,
  })).isRequired,
  fallbackReason: PropTypes.string.isRequired,
  isInWatchlist: PropTypes.func.isRequired,
  results: PropTypes.arrayOf(PropTypes.shape({
    slug: PropTypes.string.isRequired,
  })).isRequired,
  searchResult: PropTypes.shape({
    mode: PropTypes.string,
    displayLimit: PropTypes.number,
    topScore: PropTypes.number,
  }),
  searchStatus: PropTypes.string.isRequired,
  selectTag: PropTypes.func.isRequired,
  selectedTagDetails: PropTypes.arrayOf(PropTypes.shape({
    slug: PropTypes.string.isRequired,
    tag: PropTypes.string.isRequired,
  })).isRequired,
  semanticFallback: PropTypes.bool.isRequired,
  showBlockingLoading: PropTypes.bool.isRequired,
  showsLikeTitle: PropTypes.string,
  state: PropTypes.shape({
    query: PropTypes.string,
    selectedTags: PropTypes.arrayOf(PropTypes.string),
    sort: PropTypes.string.isRequired,
  }).isRequired,
  submitSemantic: PropTypes.func.isRequired,
  toggleWatchlist: PropTypes.func.isRequired,
  unknownSemanticSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
};

ArchiveSearchResults.propTypes = ArchiveSearchResultsContent.propTypes;
